// The master switch for election-day mode.
//
// Persisted in the `election_phase` Postgres table (single-row,
// id=1 guard, CHECK constraint on the phase column). Read via
// /api/election-phase (public, edge-cached), written via
// /api/admin/election-phase (auth).
//
// Active phases (day order):
//   normal                — the standard dashboard (default, pre-election)
//   polls_open            — voting in progress; turnout-focused composition
//   results_pending       — polls closed, votes turning, no call yet;
//                           blog-led composition (added in Stage 3A)
//   results_pending_rogue — Stage A variant of results_pending. Same
//                           bands, but the live-feed slot in LiveCore
//                           is REPLACED by a "Lovin Malta live sample"
//                           certainty panel (rolling sampling tally +
//                           Wilson 95% interval + INSUFFICIENT /
//                           DIRECTIONAL / PROJECTED ladder). The
//                           live-feed moves down to its own band. The
//                           panel is display-only: it never writes
//                           nationalCall — a human still calls the
//                           result via the existing /admin/announce
//                           flow.
//   counting              — official counting underway; blog + result widgets
//
// The DB CHECK constraint also still accepts the legacy 'result' value
// so any existing row stays valid, but the active TypeScript phase
// union DOES NOT include it — the winner announcement is now driven
// by editorial `nationalCall` (Stage 3A precedence rule) across both
// results_pending(_rogue) and counting, not by a dedicated phase.

export const ELECTION_PHASES = [
  'normal',
  'polls_open',
  'results_pending',
  'results_pending_rogue',
  'counting',
  // Stage 1 — sibling counting compositions. Same layout as
  // `counting`, branched on Hero copy / extras only (not three
  // near-duplicate compositions):
  //   counting_over    — "Counting has concluded" framing while we
  //                      wait for the Electoral Commission to publish.
  //   official_results — counting_over framing + a prominent link
  //                      to /official-results (the new commission-
  //                      published-result composition page).
  'counting_over',
  'official_results',
  // Post-election archive — the election is over and the homepage
  // becomes a static record: the map, the round-by-round count
  // tables, and the elected candidates per district. No LiveCore,
  // no LiveFeed, no LiveDebatePlayer, no CountingStream. Mounted by
  // HomepagePostElection in App.tsx.
  'post_election',
] as const;

export type ElectionPhase = (typeof ELECTION_PHASES)[number];

export function isElectionPhase(value: unknown): value is ElectionPhase {
  return (
    typeof value === 'string' &&
    (ELECTION_PHASES as readonly string[]).includes(value)
  );
}

export interface ElectionPhaseState {
  phase: ElectionPhase;
  updatedAt: string;
}
